import { type ForwardRefExoticComponent, Fragment, forwardRef } from 'react';

import { useDecoratedSlotContent } from '../../hooks/use-decorated-slot-content';
import { TextButtonIcon } from './text-button-icon';
import { TextButtonLabel } from './text-button-label';
import { TextButtonRoot } from './text-button-root';
import type { TextButtonPropsInterface } from './types/text-button.types';

/**
 * @since 5.0.0
 */

export const TextButton = forwardRef<HTMLButtonElement, TextButtonPropsInterface>(
  (props: TextButtonPropsInterface, forwardedRef) => {
    const { asChild = false, children, disabled, icon, iconPosition = 'start', ...rest } = props;

    const content = useDecoratedSlotContent<'button'>({
      children,
      asChild,
      template: (childContent) => (
        <Fragment>
          {icon && iconPosition === 'start' ? <TextButton.Icon>{icon}</TextButton.Icon> : null}
          <TextButton.Label>{childContent}</TextButton.Label>
          {icon && iconPosition === 'end' ? <TextButton.Icon>{icon}</TextButton.Icon> : null}
        </Fragment>
      ),
      dependencies: [icon, iconPosition],
    });

    return (
      <TextButton.Root ref={forwardedRef} asChild={asChild} disabled={disabled} {...rest}>
        {content}
      </TextButton.Root>
    );
  },
) as ForwardRefExoticComponent<TextButtonPropsInterface> & {
  Root: typeof TextButtonRoot;
  Icon: typeof TextButtonIcon;
  Label: typeof TextButtonLabel;
};

TextButton.displayName = 'TextButton';

TextButton.Root = TextButtonRoot;
TextButton.Icon = TextButtonIcon;
TextButton.Label = TextButtonLabel;
